/**
 * Color Picker Component
 * Generic color input with hex validation
 */

import React, { useState, useEffect } from 'react';
import { Label } from '@/core/components/ui/label';
import { Input } from '@/core/components/ui/input';
import { isValidHexColor } from '../../utils/validation-rules';

interface ColorPickerProps {
  /** Label for the color picker */
  label: string;

  /** Current color value (hex) */
  value: string;

  /** Callback when color changes */
  onChange: (color: string) => void;

  /** Optional description */
  description?: string;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Optional CSS class */
  className?: string;
}

/**
 * Color Picker Component
 * Provides both color input and hex text input
 */
export function ColorPicker({
  label,
  value,
  onChange,
  description,
  disabled = false,
  className = '',
}: ColorPickerProps) {
  const [hexValue, setHexValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  // Sync with external value changes
  useEffect(() => {
    setHexValue(value);
    setIsValid(isValidHexColor(value));
  }, [value]);

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setHexValue(newColor);
    setIsValid(true);
    onChange(newColor);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Auto-add # prefix if missing
    if (newValue && !newValue.startsWith('#')) {
      newValue = `#${newValue}`;
    }

    setHexValue(newValue);

    // Validate and update
    if (isValidHexColor(newValue)) {
      setIsValid(true);
      onChange(newValue);
    } else {
      setIsValid(false);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={`color-${label}`} className="text-sm font-medium">
        {label}
      </Label>

      <div className="flex items-center gap-3">
        {/* Color input */}
        <div className="relative">
          <input
            type="color"
            id={`color-${label}`}
            value={hexValue}
            onChange={handleColorInputChange}
            disabled={disabled}
            className="h-10 w-16 cursor-pointer rounded border border-border bg-background disabled:cursor-not-allowed disabled:opacity-50"
            style={{ padding: '2px' }}
          />
        </div>

        {/* Hex input */}
        <Input
          type="text"
          value={hexValue}
          onChange={handleHexInputChange}
          disabled={disabled}
          placeholder="#000000"
          maxLength={7}
          className={`flex-1 font-mono text-sm ${!isValid ? 'border-red-500' : ''}`}
        />
      </div>

      {/* Description or error */}
      {description && isValid && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {!isValid && (
        <p className="text-xs text-red-500">Invalid hex color format (e.g., #FF0000)</p>
      )}
    </div>
  );
}
